import type { ItineraryItemEventModel } from '@elara/client-core/models/ItineraryItemEventModel';
import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';
import type { BookingDisplayStatus } from '@elara/domain/booking-status';
import type { FlightSegment } from '@elara/domain/models/schemas/flight-schemas';
import type { WidgetImage } from '@elara/domain/models/schemas/widget-schemas';

import type { IconName } from '#ui/MaterialIcon.js';

import { useState } from 'react';

import { getEventTypeIcon } from '@elara/client-core/lib/itinerary-utils';
import { getPlaceIcon } from '@elara/client-core/lib/place-category';
import { cn, getEmojiFlag } from '@elara/client-core/lib/utils';
import { bookingDisplayStatusLabel, isUnbookedNormEventType } from '@elara/domain/booking-status';

import { bookingStatusBorderColor, bookingStatusLabelColor } from '#lib/booking-status.js';
import { useItineraryState } from '#states/hooks.js';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { ItineraryLargeBlockLayout } from './ItineraryLargeBlockLayout.js';

export interface ItineraryItemAction {
  type: 'primary' | 'secondary' | 'tertiary';
  label: string;
  icon?: IconName;
  destructive?: boolean;
  onClick: (e: React.MouseEvent) => void;
}

export interface BaseItineraryBlockHeaderAttributes {
  icon?: IconName;
  label: string;
}

export interface BaseItineraryBlockProps {
  event: ItineraryItemEventModel;
  items: ItineraryItem[];

  size?: 'sm' | 'default' | 'lg';
  children?: React.ReactNode;

  canDeleteItem?: boolean;
  actions?: ItineraryItemAction[];
  /** When true, renders actions as icon buttons instead of a dropdown menu (e.g. for unavailable items). */
  compactActions?: boolean;

  title?: string;
  headerAttributes?: BaseItineraryBlockHeaderAttributes[];
  images?: WidgetImage[];

  /** Overrides the header booking status (e.g. "No longer available" for an unavailable item). */
  bookingStatusOverride?: BookingDisplayStatus;

  /** When provided and status is "planned", renders a link button in the header instead of the "Not booked" label. */
  headerBookAction?: { label: string; onClick: () => void };

  /** Rendered between the description and the action buttons in the expanded panel. */
  summary?: React.ReactNode;

  preHeaderContent?: React.ReactNode;

  /** When provided, the formatted-time chip in the condensed header becomes a clickable button (e.g. to open a reschedule modal). */
  onTimeClick?: () => void;

  /** Overrides the condensed header's leading time-chip content (defaults to `event.formattedTime`).
   * A plain string renders as the clickable reschedule button; richer content (e.g. a hotel's
   * two-line "From" / check-in time) renders as plain text. Pass `null` when the event has no time —
   * the column is still RESERVED (empty), so the row's icon and title stay in line with the rows that
   * do have one. To omit the column altogether, see {@link ownsTimeColumn}. */
  leadingTime?: React.ReactNode;

  /**
   * Set when the block draws its own time column — a flight's multi-leg timeline gives every leg its
   * own `w-18` chip, so the shared one is omitted rather than reserved. Reserving it as well would
   * indent the whole flight row by a column it already has.
   */
  ownsTimeColumn?: boolean;

  className?: string;

  panelContent?: React.ReactNode;
  panelPosition?: 'left' | 'right';

  onClick?: (e: React.MouseEvent) => void;
}

export function BaseItineraryBlock({
  event,
  items,
  size = 'default',
  canDeleteItem = false,
  actions,
  compactActions = false,
  children,
  title,
  headerAttributes,
  bookingStatusOverride,
  headerBookAction,
  summary,
  preHeaderContent,
  onTimeClick,
  leadingTime,
  ownsTimeColumn = false,
  className,
  panelContent,
  panelPosition = 'left',
  onClick,
}: BaseItineraryBlockProps) {
  const itineraryState = useItineraryState();
  const [isOpen, setIsOpen] = useState(false);

  const primaryItem = items[0]!;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e);
    }
  };

  const buttonActions: ItineraryItemAction[] = [];
  const dropdownMenuActions: ItineraryItemAction[] = [];

  if (actions) {
    buttonActions.push(...actions.filter((action) => action.type === 'primary' || action.type === 'secondary'));
    dropdownMenuActions.push(...actions.filter((action) => action.type === 'tertiary'));
  }

  if (canDeleteItem) {
    if (items.length <= 1) {
      if (primaryItem.isItemRemovable) {
        dropdownMenuActions.push({
          type: 'tertiary',
          label: 'Remove from itinerary',
          icon: 'delete',
          destructive: true,
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();

            itineraryState.openDeleteDialog(primaryItem);
          },
        });
      }
    } else {
      for (const item of items.filter((i) => i.isItemRemovable)) {
        dropdownMenuActions.push({
          type: 'tertiary',
          label: `Remove ${item.displayTitle}`,
          icon: 'delete',
          destructive: true,
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();

            itineraryState.openDeleteDialog(item);
          },
        });
      }
    }
  }

  function renderActionsMenu(className?: string) {
    if (dropdownMenuActions.length === 0) return null;

    // Compact mode: render button actions as icon buttons (e.g. refresh for unavailable items)
    if (compactActions) {
      // Single item: icon buttons for everything (actions + delete), no dropdown
      if (items.length <= 1) {
        return (
          <div className={cn('flex items-center gap-1 flex-shrink-0', className)}>
            {buttonActions.map((action) => (
              <Button
                key={action.label}
                variant="icon"
                size="icon-sm"
                tooltip={action.label}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(e);
                }}
              >
                {action.icon && <MaterialIcon icon={action.icon} />}
              </Button>
            ))}
            {primaryItem.isItemRemovable && (
              <Button
                variant="icon"
                size="icon-sm"
                tooltip="Remove"
                onClick={(e) => {
                  e.stopPropagation();
                  itineraryState.openDeleteDialog(primaryItem);
                }}
              >
                <MaterialIcon icon="delete" className="text-destructive" />
              </Button>
            )}
          </div>
        );
      }

      // Multiple items: icon buttons for actions, dropdown for per-item deletes
      return (
        <div className={cn('flex items-center gap-1 flex-shrink-0', className)}>
          {buttonActions.map((action) => (
            <Button
              key={action.label}
              variant="icon"
              size="icon-sm"
              tooltip={action.label}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick(e);
              }}
            >
              {action.icon && <MaterialIcon icon={action.icon} />}
            </Button>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="icon" size="icon-sm">
                <MaterialIcon icon="more_horiz" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {dropdownMenuActions.map((item, index) => (
                <DropdownMenuItem
                  key={`${item.label}-${index}`}
                  onClick={(e) => {
                    setTimeout(() => {
                      item.onClick?.(e);
                    }, 50);
                  }}
                  className={item.destructive ? 'text-destructive' : ''}
                >
                  {item.icon && <MaterialIcon icon={item.icon} className="mr-2" />}
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    }

    return (
      <div className={cn('flex items-center gap-2 flex-shrink-0', className)}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="icon" size="icon-sm">
              <MaterialIcon icon="more_horiz" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {dropdownMenuActions.map((item, index) => (
              <DropdownMenuItem
                key={`${item.label}-${index}`}
                onClick={(e) => {
                  setTimeout(() => {
                    item.onClick?.(e);
                  }, 50);
                }}
                className={item.destructive ? 'text-destructive' : ''}
              >
                {item.icon && <MaterialIcon icon={item.icon} className="mr-2" />}
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  let icon = <MaterialIcon icon={getEventTypeIcon(event.type)} size={IconSizes.xs} />;

  if (event.type === 'flight_departure') {
    const segment = event.metadata?.segment as FlightSegment;

    if (segment.airline.logo_url) {
      icon = (
        <Avatar className="h-4 w-4 rounded-none">
          <AvatarImage src={segment.airline.logo_url} alt={segment.airline.name || ''} loading="lazy" />
          <AvatarFallback className="text-xs font-medium uppercase rounded-sm bg-secondary text-secondary-foreground">
            {segment.airline.iata_code}
          </AvatarFallback>
        </Avatar>
      );
    }
  }

  if (event.type === 'destination_arrival') {
    const segment = event.metadata?.segment as FlightSegment;
    const countryCode = segment.destination?.country_code;

    if (countryCode) {
      icon = <div className="text-xs w-4">{getEmojiFlag(countryCode)}</div>;
    }
  }

  if (primaryItem.isPlace && primaryItem.place && primaryItem.place.type) {
    icon = <MaterialIcon icon={getPlaceIcon(primaryItem.place.type)} size={IconSizes.xs} />;
  }

  const isCard = !['flight_arrival', 'destination_arrival', 'hotel_checkout', 'layover'].includes(event.type);

  if (size === 'sm') {
    // No expand/collapse state at this size (unlike 'default', which only mutes while collapsed) —
    // a cancelled/unavailable item here (e.g. a layover next to a cancelled flight leg) just always
    // recedes visually.
    const headerStatus = bookingStatusOverride ?? event.bookingStatus;
    const isMuted = headerStatus === 'cancelled' || headerStatus === 'unavailable';

    return (
      <div
        className={cn(
          'relative flex items-center gap-4 justify-between shadow-none rounded-md text-sm',
          isCard ? 'bg-background border py-1 px-3 min-h-[46px]' : 'px-3 py-2.5',
          onClick && 'cursor-pointer hover:shadow-sm',
          isMuted && 'opacity-50',
          'transition-all duration-200 ease-in-out',
          className,
        )}
      >
        <div className={cn('flex-1 flex items-center gap-2')} onClick={handleClick}>
          {icon}
          <span className={cn(isCard && 'font-medium')}>{title ?? event.title}</span>
        </div>

        {renderActionsMenu('-mr-1')}
      </div>
    );
  }

  /** Default size */
  if (size === 'default') {
    const isUnavailable = bookingStatusOverride === 'unavailable';

    // Status badge / book button — shared between both header layouts.
    const headerStatus = bookingStatusOverride ?? event.bookingStatus;
    // Places, activities, and custom items only surface a status once it moves past "planned" —
    // an unbooked place/activity is the norm, so a "Not booked" label would just be noise.
    const isUnbookedNorm = isUnbookedNormEventType(event.type);
    const showStatus = headerStatus && !(isUnbookedNorm && headerStatus === 'planned');
    const headerStatusEl =
      showStatus &&
      headerStatus &&
      (headerStatus === 'planned' && headerBookAction ? (
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            headerBookAction.onClick();
          }}
        >
          {headerBookAction.label}
        </Button>
      ) : (
        <div
          className={cn('font-medium inline-flex items-center gap-1 text-xs', bookingStatusLabelColor(headerStatus))}
        >
          {bookingDisplayStatusLabel(headerStatus)}
        </div>
      ));

    // Leading time chip: an explicit `leadingTime` override wins (a string label, or `null` for an
    // event with no time); otherwise falls back to the event's own formatted time, same as before.
    // A falsy result still occupies the column — see the render below.
    const resolvedLeadingTime =
      leadingTime !== undefined ? leadingTime : !preHeaderContent ? event.formattedTime : undefined;

    // A cancelled/unavailable item recedes visually while collapsed — expanding it (to view/restore
    // details) brings it back to full opacity so its content reads clearly.
    const isMuted = (headerStatus === 'cancelled' || headerStatus === 'unavailable') && !isOpen;

    return (
      <div
        className={cn(
          'relative ii-card flex flex-col overflow-x-hidden rounded-md shadow-sm bg-background hover:shadow-md border-l-3',
          bookingStatusBorderColor(headerStatus, !isUnbookedNorm),
          isOpen && 'shadow-md',
          isMuted && 'opacity-50 shadow-xs',
          'transition-all duration-200 ease-in-out',
          className,
        )}
      >
        <div
          className={cn('flex gap-3 w-full px-3 py-2.5 cursor-pointer items-center')}
          onClick={() => setIsOpen(!isOpen)}
        >
          {/* Leading: the formatted time chip, shared by every event kind. A plain string renders as
              the original clickable (reschedule) button; richer content — e.g. a hotel's two-line
              "From" / check-in time — renders as plain text instead, since the button's fixed height
              would clip a second line. An event with NO time keeps the column as whitespace: omitting
              it pulled the icon and title left, leaving a timeless row out of line with its
              neighbours. Only a block that draws its own column (`ownsTimeColumn`) skips it. */}
          {!ownsTimeColumn &&
            (!resolvedLeadingTime ? (
              <div className="w-18 flex-shrink-0" aria-hidden="true" data-testid="itinerary-time-column" />
            ) : typeof resolvedLeadingTime === 'string' ? (
              <div className="w-18 flex-shrink-0 flex items-center" data-testid="itinerary-time-column">
                <Button
                  size="xs"
                  variant="ghost"
                  className="tabular-nums font-normal text-muted-foreground -mx-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTimeClick?.();
                  }}
                >
                  {resolvedLeadingTime}
                </Button>
              </div>
            ) : (
              <div
                className="w-18 flex-shrink-0 flex items-center text-xs text-muted-foreground leading-tight"
                data-testid="itinerary-time-column"
              >
                {resolvedLeadingTime}
              </div>
            ))}

          {/* Content: icon + title (every kind but flights, whose timeline speaks for itself),
              then attributes and any custom content (a flight's departure→arrival→airline timeline,
              a place's address, …). */}
          <div className="space-y-1 flex-1 min-w-0">
            {!primaryItem.isFlight && (
              <div className="flex items-center gap-2 font-medium text-sm">
                <span className="flex-shrink-0 flex items-center text-muted-foreground">{icon}</span>
                <span className="truncate">{title ?? event.title}</span>
              </div>
            )}
            {headerAttributes && (
              <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground">
                {headerAttributes.map((attr) => (
                  <div key={attr.label} className="flex items-center gap-1">
                    {attr.icon && <MaterialIcon icon={attr.icon} size={IconSizes.xxs} />}
                    {attr.label}
                  </div>
                ))}
              </div>
            )}
            {preHeaderContent}
          </div>

          <div className="flex items-center justify-end gap-1.5 flex-shrink-0 min-w-20">{headerStatusEl}</div>

          <MaterialIcon
            icon="keyboard_arrow_down"
            className={cn('flex-shrink-0 text-muted-foreground transition-transform rotate-0', isOpen && 'rotate-180')}
          />
        </div>

        <div className={cn('flex flex-col gap-4 px-2.5 pb-3 pt-1', !isOpen && 'hidden')}>
          {summary}

          {/* Actions — sit between the description and the detail content. */}
          {(buttonActions.length > 0 || dropdownMenuActions.length > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              {buttonActions.map((action) =>
                action.destructive ? (
                  // Destructive actions (e.g. Remove) render as a compact icon button.
                  <Button
                    key={action.label}
                    variant="icon"
                    size="icon-sm"
                    tooltip={action.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick(e);
                    }}
                  >
                    {action.icon && <MaterialIcon icon={action.icon} className="text-destructive" />}
                  </Button>
                ) : (
                  <Button
                    key={action.label}
                    size="sm"
                    variant={action.type === 'primary' ? 'default' : 'outline'}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick(e);
                    }}
                  >
                    {action.icon && <MaterialIcon icon={action.icon} />}
                    {action.label}
                  </Button>
                ),
              )}
              {renderActionsMenu()}
            </div>
          )}

          {children}
        </div>
      </div>
    );
  }

  /** Large size */
  // Every action (including the overflow ones and Remove) is a full button in one row after the
  // detail rows: on the full page there is room, and a lone "…" menu under the title got lost.
  const largeActions = [...buttonActions, ...dropdownMenuActions];
  return (
    <ItineraryLargeBlockLayout
      icon={icon}
      typeLabel={event.formattedType}
      preHeaderContent={preHeaderContent}
      title={title ?? event.title}
      headerAttributes={headerAttributes}
      summary={summary}
      panelContent={panelContent}
      panelPosition={panelPosition}
      className={className}
    >
      {children}
      {largeActions.length > 0 && (
        // Buttons are screen-only; a printed itinerary keeps just the details.
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          {largeActions.map((action) => (
            <Button
              key={action.label}
              variant={action.type === 'primary' ? 'default' : 'outline'}
              className={cn(
                // Outline's hover fills with the gold accent; a red action gets a faint red wash instead.
                action.destructive &&
                  'text-destructive hover:text-destructive hover:bg-destructive/10 hover:border-destructive/40',
              )}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick(e);
              }}
            >
              {action.icon && <MaterialIcon icon={action.icon} />}
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </ItineraryLargeBlockLayout>
  );
}
